import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, X, Check, Sprout, Trash2, Loader2, AlertTriangle, RefreshCw } from "lucide-react";
import api, { TIER_META, OCCASION_META } from "../lib/api";
import AdminLabs from "../components/AdminLabs";

export default function Admin() {
  const [metrics, setMetrics] = useState(null);
  const [flags, setFlags] = useState([]);
  const [seeds, setSeeds] = useState([]);
  const [seedForm, setSeedForm] = useState({ content_type: "text", tier: "savage", content: "", region: "" });
  const [seeding, setSeeding] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulking, setBulking] = useState(false);
  const [rereviewing, setRereviewing] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [feedMeta, setFeedMeta] = useState(null);
  const [vipId, setVipId] = useState("");
  const [vipSubs, setVipSubs] = useState([]);
  const [newTheme, setNewTheme] = useState({ label: "", description: "", occasion: "" });

  const load = useCallback(async () => {
    const [m, f, s, c] = await Promise.all([api.get("/admin/metrics"), api.get("/admin/moderation"), api.get("/admin/seeds"), api.get("/admin/config")]);
    setMetrics(m.data); setFlags(f.data); setSeeds(s.data); setCfg(c.data);
    api.get("/feed/meta").then((r) => setFeedMeta(r.data)).catch(() => {});
    api.get("/admin/vip-submissions").then((r) => setVipSubs(r.data)).catch(() => {});
  }, []);

  const markVip = async (id, val = true) => {
    const sid = id || vipId.trim();
    if (!sid) return toast.error("Enter a submission ID");
    try {
      await api.post(`/admin/submissions/${sid}/vip`, { is_vip: val });
      toast.success(val ? "Marked VIP ⭐" : "VIP removed");
      setVipId("");
      const r = await api.get("/admin/vip-submissions"); setVipSubs(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleDiscovery = async (enabled) => {
    try { await api.post("/admin/feed-discovery", { enabled }); setFeedMeta((m) => ({ ...m, discovery_enabled: enabled })); toast.success(`Discovery tabs ${enabled ? "on" : "off"}`); }
    catch (e) { toast.error("Couldn't update"); }
  };

  const saveCfg = async (patch) => {
    try { const r = await api.put("/admin/config", patch); setCfg(r.data); }
    catch (e) { toast.error("Couldn't save"); }
  };
  const addTheme = async () => {
    if (!cfg) return;
    if (!newTheme.label.trim()) { toast.error("Theme needs a label"); return; }
    const t = { id: "theme_" + Date.now().toString(36), label: newTheme.label.trim(), description: newTheme.description.trim(), occasion: newTheme.occasion || null };
    const themes = [...(cfg.themes || []), t];
    await saveCfg({ themes });
    setNewTheme({ label: "", description: "", occasion: "" });
    toast.success("Theme added");
  };
  const activateTheme = async (id) => { if (!cfg) return; await saveCfg({ active_theme_id: cfg.active_theme_id === id ? "" : id }); };
  const removeTheme = async (id) => {
    if (!cfg) return;
    await saveCfg({ themes: (cfg.themes || []).filter((t) => t.id !== id), active_theme_id: cfg.active_theme_id === id ? "" : cfg.active_theme_id });
  };
  const saveBonus = async (bw) => { await saveCfg({ bonus_window: bw }); toast.success("Bonus window updated"); };
  useEffect(() => { load(); }, [load]);

  const resolve = async (flagId, action) => {
    try {
      await api.post(`/admin/moderation/${flagId}/resolve`, { action });
      toast.success(action === "approve" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const submitSeed = async () => {
    if (!seedForm.content.trim()) { toast.error("Add some content to roast"); return; }
    setSeeding(true);
    try {
      await api.post("/admin/seed-submission", { ...seedForm, region: seedForm.region || undefined });
      toast.success("Seed added to the queue 🌱");
      setSeedForm({ content_type: "text", tier: "savage", content: "", region: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add seed"); }
    finally { setSeeding(false); }
  };
  const deleteSeed = async (id) => {
    try { await api.delete(`/admin/seeds/${id}`); toast.success("Seed removed"); load(); }
    catch (e) { toast.error("Couldn't remove seed"); }
  };
  const onSeedImage = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setSeedForm((f) => ({ ...f, content: reader.result }));
    reader.readAsDataURL(file);
  };
  const submitBulk = async () => {
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) { toast.error("Paste at least one line"); return; }
    setBulking(true);
    try {
      const r = await api.post("/admin/seed-submission/bulk", { lines, tier: seedForm.tier, region: seedForm.region || undefined });
      const rej = r.data.rejected?.length || 0;
      toast.success(`Added ${r.data.created} seed${r.data.created === 1 ? "" : "s"} 🌱${rej ? ` · ${rej} rejected by moderation` : ""}`);
      setBulkText(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk import failed"); }
    finally { setBulking(false); }
  };
  const runRereview = async () => {
    setRereviewing(true);
    try {
      const r = await api.post("/admin/rubric-rereview");
      toast.success(`Re-review complete — scanned ${r.data.scanned}, flagged ${r.data.flagged} for manual review`);
      load();
    } catch (e) { toast.error("Re-review pass failed"); }
    finally { setRereviewing(false); }
  };

  const fmt = (v, s = "") => (v === null || v === undefined ? "—" : `${v}${s}`);

  const cards = metrics ? [
    { l: "Submissions", v: metrics.submissions_created },
    { l: "Sub→Paid conv.", v: `${(metrics.submission_to_paid_conversion * 100).toFixed(0)}%` },
    { l: `Revenue (${metrics.settlement_currency})`, v: `${metrics.revenue.toFixed(2)}` },
    { l: `Payouts (${metrics.settlement_currency})`, v: `${metrics.total_payouts.toFixed(2)}` },
    { l: `Margin (${metrics.settlement_currency})`, v: `${metrics.margin.toFixed(2)}` },
    { l: "Avg → first claim", v: fmt(metrics.avg_seconds_to_first_claim, "s") },
    { l: "Avg → first roast", v: fmt(metrics.avg_seconds_to_first_roast, "s") },
    { l: "Region override", v: `${(metrics.region_override_rate * 100).toFixed(0)}%` },
    { l: "Flags open", v: `${metrics.moderation_flags_open}/${metrics.moderation_flags_total}` },
    { l: "Avg flag resolve", v: fmt(metrics.avg_seconds_flag_resolution, "s") },
    { l: "Published", v: metrics.published_feed_items },
    { l: "Share rate", v: `${(metrics.share_rate * 100).toFixed(0)}%` },
    { l: "Upvote rate", v: `${(metrics.upvote_rate * 100).toFixed(0)}%` },
  ] : [];

  return (
    <div>
      <div className="flex items-center gap-2 mb-2"><ShieldCheck className="text-brand-accent" size={22} /><h1 className="font-heading font-bold uppercase tracking-tighter text-4xl sm:text-5xl">Admin</h1></div>
      <p className="text-zinc-400 mt-2 mb-8">Live metrics and the moderation review queue.</p>

      <AdminLabs />

      <h2 className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4">Metrics</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12">
        {cards.map((c) => (
          <div key={c.l} data-testid={`metric-${c.l}`} className="rounded-sm border border-white/10 bg-zinc-900/60 p-4">
            <div className="font-heading text-2xl font-bold">{c.v}</div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-widest mt-1">{c.l}</div>
          </div>
        ))}
      </div>

      <h2 className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4">Feed discovery (trending/newest tabs)</h2>
      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5 mb-12" data-testid="feed-discovery-admin">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm text-zinc-300">
            {feedMeta ? <>Published roasts: <b className="text-white">{feedMeta.published_count}</b> / {feedMeta.threshold} needed.
              {feedMeta.published_count < feedMeta.threshold && <span className="text-zinc-500"> Below threshold — recommended off until you hit {feedMeta.threshold}.</span>}</> : "Loading…"}
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input data-testid="feed-discovery-toggle" type="checkbox" checked={!!feedMeta?.discovery_enabled} onChange={(e) => toggleDiscovery(e.target.checked)} className="accent-[#ff4500] w-4 h-4" />
            Show discovery tabs
          </label>
        </div>
      </div>

      <h2 className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4">VIP Roast fast-track</h2>
      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5 mb-12" data-testid="vip-admin">
        <p className="text-sm text-zinc-400 mb-3">Flag an invited creator's submission for priority pickup by Top/Legend roasters.</p>
        <div className="flex gap-2">
          <input data-testid="vip-sub-id" value={vipId} onChange={(e) => setVipId(e.target.value)} placeholder="Submission ID"
            className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
          <button data-testid="vip-mark-btn" onClick={() => markVip()} className="px-5 py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm">Make VIP</button>
        </div>
        {vipSubs.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="vip-list">
            {vipSubs.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 rounded-sm border border-brand/20 bg-brand/5 px-3 py-2 text-sm">
                <span className="text-zinc-300 truncate">⭐ {s.id} · {s.tier}</span>
                <button data-testid={`vip-remove-${s.id}`} onClick={() => markVip(s.id, false)} className="text-zinc-500 hover:text-destructive text-xs">Remove</button>
              </div>
            ))}
          </div>
        )}
      </div>


      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5 mb-6">
        <p className="text-xs text-zinc-500 mb-4">Post house submissions so the roaster queue is never empty before your beta invite. Seeds are moderated, don't count as revenue, and never auto-expire.</p>
        <div className="flex gap-2 mb-3">
          {["text", "image"].map((t) => (
            <button key={t} data-testid={`seed-type-${t}`} onClick={() => setSeedForm((f) => ({ ...f, content_type: t, content: "" }))}
              className={`px-4 py-2 rounded-full text-sm font-bold capitalize transition-colors ${seedForm.content_type === t ? "bg-brand text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10"}`}>{t}</button>
          ))}
        </div>
        {seedForm.content_type === "text" ? (
          <textarea data-testid="seed-content" value={seedForm.content} onChange={(e) => setSeedForm((f) => ({ ...f, content: e.target.value }))}
            placeholder="A dating bio, a startup pitch, a spicy tweet…" rows={3}
            className="w-full rounded-sm bg-black/40 border border-white/10 p-3 text-sm text-zinc-100 outline-none focus:border-brand mb-3" />
        ) : (
          <div className="mb-3">
            <input data-testid="seed-image" type="file" accept="image/*" onChange={onSeedImage} className="text-xs text-zinc-400" />
            {seedForm.content && <img src={seedForm.content} alt="" className="mt-2 h-24 rounded-sm border border-white/10 object-cover" />}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="seed-tier" value={seedForm.tier} onChange={(e) => setSeedForm((f) => ({ ...f, tier: e.target.value }))}
            className="rounded-sm bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-brand">
            {Object.entries(TIER_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select data-testid="seed-region" value={seedForm.region} onChange={(e) => setSeedForm((f) => ({ ...f, region: e.target.value }))}
            className="rounded-sm bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-brand">
            <option value="">Default region</option>
            {["IN", "US", "GB", "EU"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button data-testid="seed-submit" onClick={submitSeed} disabled={seeding}
            className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-black font-bold text-sm px-5 py-2 transition-colors">
            {seeding ? <Loader2 size={15} className="animate-spin" /> : <Sprout size={15} />} {seeding ? "Adding…" : "Add seed"}
          </button>
        </div>

        <div className="mt-5 pt-5 border-t border-white/10">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-1">Bulk paste</div>
          <p className="text-xs text-zinc-500 mb-3">One roast-worthy line per row (up to 50). Uses the tier & region selected above. Each line is moderated.</p>
          <textarea data-testid="bulk-content" value={bulkText} onChange={(e) => setBulkText(e.target.value)}
            placeholder={"My dating bio is just red flags in a trench coat\nOur startup is 'Uber but for X'\n…"} rows={4}
            className="w-full rounded-sm bg-black/40 border border-white/10 p-3 text-sm text-zinc-100 outline-none focus:border-brand mb-3" />
          <button data-testid="bulk-submit" onClick={submitBulk} disabled={bulking}
            className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 disabled:opacity-60 text-zinc-100 font-bold text-sm px-5 py-2 transition-colors">
            {bulking ? <Loader2 size={15} className="animate-spin" /> : <Sprout size={15} />} {bulking ? "Importing…" : "Add all to queue"}
          </button>
        </div>
      </div>
      {seeds.length > 0 && (
        <div className="space-y-2 mb-12" data-testid="seed-list">
          {seeds.map((s) => (
            <div key={s.id} data-testid={`seed-${s.id}`} className="flex items-center justify-between gap-3 rounded-sm border border-white/10 bg-zinc-900/40 px-4 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: TIER_META[s.tier]?.color }}>{TIER_META[s.tier]?.label}</span>
                  <span className="text-[11px] text-zinc-500 uppercase tracking-widest">{s.status} · {s.roast_count} roasts</span>
                </div>
                <p className="text-sm text-zinc-300 truncate mt-0.5">{s.content_type === "image" ? "🖼️ image submission" : s.content}</p>
              </div>
              <button data-testid={`seed-delete-${s.id}`} onClick={() => deleteSeed(s.id)} className="shrink-0 text-zinc-500 hover:text-destructive transition-colors"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      <h2 className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4">Engagement — themed rounds &amp; bonus windows</h2>
      <div className="rounded-sm border border-white/10 bg-zinc-900/60 p-5 mb-6" data-testid="theme-calendar">
        <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-3">Themed rounds</div>
        <div className="space-y-2 mb-4">
          {(cfg?.themes || []).length === 0 && <p className="text-xs text-zinc-500">No themes yet. Create one below.</p>}
          {(cfg?.themes || []).map((t) => (
            <div key={t.id} data-testid={`theme-${t.id}`} className="flex items-center justify-between gap-3 rounded-sm border border-white/10 bg-zinc-900/40 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 font-semibold truncate">{t.label} {t.occasion && <span className="text-xs text-zinc-500">· {OCCASION_META[t.occasion]?.emoji} {OCCASION_META[t.occasion]?.label}</span>}</div>
                {t.description && <div className="text-xs text-zinc-500 truncate">{t.description}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`activate-${t.id}`} onClick={() => activateTheme(t.id)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${cfg.active_theme_id === t.id ? "bg-brand text-black" : "bg-white/10 text-zinc-200 hover:bg-white/20"}`}>
                  {cfg.active_theme_id === t.id ? "Active" : "Activate"}
                </button>
                <button data-testid={`remove-theme-${t.id}`} onClick={() => removeTheme(t.id)} className="text-zinc-500 hover:text-destructive"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input data-testid="theme-label" value={newTheme.label} onChange={(e) => setNewTheme((n) => ({ ...n, label: e.target.value }))} placeholder="e.g. Mild Monday"
            className="rounded-sm bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-brand" />
          <select data-testid="theme-occasion" value={newTheme.occasion} onChange={(e) => setNewTheme((n) => ({ ...n, occasion: e.target.value }))}
            className="rounded-sm bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-brand">
            <option value="">No occasion</option>
            {Object.entries(OCCASION_META).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
          </select>
          <input data-testid="theme-desc" value={newTheme.description} onChange={(e) => setNewTheme((n) => ({ ...n, description: e.target.value }))} placeholder="Short prompt (optional)"
            className="sm:col-span-2 rounded-sm bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-brand" />
        </div>
        <button data-testid="add-theme" onClick={addTheme} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm px-5 py-2 transition-colors">Add theme</button>

        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-3">Bonus roaster window</div>
          {cfg && (
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex items-center gap-2 text-sm text-zinc-200">
                <input data-testid="bonus-active" type="checkbox" checked={!!cfg.bonus_window?.active} onChange={(e) => saveBonus({ ...cfg.bonus_window, active: e.target.checked })} className="accent-[#ff4500] w-4 h-4" /> Active
              </label>
              <div>
                <div className="text-[11px] text-zinc-500 mb-1">Multiplier</div>
                <input data-testid="bonus-multiplier" type="number" step="0.1" min="1" defaultValue={cfg.bonus_window?.multiplier || 1.5}
                  onBlur={(e) => saveBonus({ ...cfg.bonus_window, multiplier: parseFloat(e.target.value) || 1.5 })}
                  className="w-24 rounded-sm bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-brand" />
              </div>
              <div>
                <div className="text-[11px] text-zinc-500 mb-1">Label</div>
                <input data-testid="bonus-label" defaultValue={cfg.bonus_window?.label || ""} placeholder="e.g. Happy Hour"
                  onBlur={(e) => saveBonus({ ...cfg.bonus_window, label: e.target.value })}
                  className="rounded-sm bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-brand" />
              </div>
              <div>
                <div className="text-[11px] text-zinc-500 mb-1">Honest end time</div>
                <input data-testid="bonus-ends" type="datetime-local" defaultValue={cfg.bonus_window?.ends_at ? cfg.bonus_window.ends_at.slice(0, 16) : ""}
                  onBlur={(e) => saveBonus({ ...cfg.bonus_window, ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  className="rounded-sm bg-black/40 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-brand" />
              </div>
            </div>
          )}
          <p className="text-[11px] text-zinc-600 mt-2">Boosted rate is locked in when a roaster submits during the window. No fake countdowns — the end time shown to roasters is exactly this.</p>
        </div>
      </div>

      <div className="rounded-sm border border-brand/25 bg-brand/5 p-5 mb-6" data-testid="reviewer-guidelines">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-xs tracking-[0.2em] uppercase text-zinc-300 font-bold flex items-center gap-2"><ShieldCheck size={15} className="text-brand" /> Reviewer guidelines</h2>
          <button data-testid="rereview-btn" onClick={runRereview} disabled={rereviewing}
            className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-black font-bold text-xs px-4 py-2 transition-colors">
            {rereviewing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {rereviewing ? "Re-reviewing…" : "Run rubric re-review pass"}
          </button>
        </div>
        <p className="text-xs text-zinc-400 mb-3">Out-of-scope categories — treat all with <span className="text-zinc-100 font-semibold">equal weight</span>. Roasting looks, career, taste and hobbies is fine; targeting <span className="text-zinc-100 font-semibold">who someone is by origin</span> is not.</p>
        <ul className="text-xs text-zinc-300 space-y-1.5 list-disc pl-5 mb-3">
          <li><span className="font-semibold">Protected characteristics:</span> race, religion, caste, disability, gender identity, sexual orientation.</li>
          <li><span className="font-semibold text-brand-accent">Regional &amp; migration identity:</span> home state/region/hometown, migrating for work/study, migrant-community &amp; caste-adjacent regional stereotypes.</li>
          <li><span className="font-semibold text-brand-accent">Political / geopolitical:</span> displacement, conflict/disputed regions, communal tension, interstate/cross-border migration stereotypes.</li>
          <li><span className="font-semibold">Always out:</span> sexual/explicit, minors, real tragedy/self-harm, threats of violence.</li>
        </ul>
        <div className="text-xs text-zinc-400">
          <span className="font-semibold text-zinc-200">Crosses the line, e.g.:</span>
          <div className="mt-1 space-y-1 pl-1">
            <div>• "Typical &lt;region&gt; person — go back to your state" (mocks origin, not a choice).</div>
            <div>• "All &lt;language/migrant community&gt; are &lt;stereotype&gt;" (community stereotype).</div>
            <div>• Punchlines leaning on a conflict/disputed region or displacement.</div>
          </div>
        </div>
      </div>

      <h2 className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold mb-4">Moderation queue</h2>
      {flags.length === 0 ? (
        <div className="rounded-sm border border-white/10 bg-zinc-900/40 p-10 text-center text-zinc-400">Queue is clear. Nothing to review.</div>
      ) : (
        <div className="space-y-3">
          {flags.map((f) => (
            <div key={f.id} data-testid={`flag-${f.id}`} className="rounded-sm border border-white/10 bg-zinc-900/60 p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-widest text-brand-accent">{f.target_type}</span>
                <span className="text-[11px] text-zinc-500">· {f.source} · {f.flag_reason}</span>
              </div>
              <p className="text-sm text-zinc-200">
                {f.target?.content_text || f.target?.content || "(content unavailable)"}
              </p>
              <div className="flex items-center gap-2 mt-4">
                <button data-testid={`approve-${f.id}`} onClick={() => resolve(f.id, "approve")}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-tier-mild/20 text-tier-mild text-sm font-bold hover:bg-tier-mild/30 transition-colors">
                  <Check size={15} /> Approve
                </button>
                <button data-testid={`reject-${f.id}`} onClick={() => resolve(f.id, "reject")}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-destructive/20 text-destructive text-sm font-bold hover:bg-destructive/30 transition-colors">
                  <X size={15} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
